import { useEffect } from "react";
import { Col, Row } from "react-bootstrap";
import { toast } from "react-toastify";
import { Currency, InputText, IntegerNumber } from "../Input";
import Checkbox from "../Checkbox";
import Slct from "../Slct";
import DatePicker from "../DatePicker";
import Required from "../Required";
import TableResponsive from "../TableResponsive";
import StatusFlag from "../StatusFlag";
import useFormat from "../../hooks/utils/useFormat";

/**
 * Editor de parcelamento (extraído do bloco "Parcelamento" do modal de
 * recebimentos). Gera as parcelas a partir da configuração (quantidade,
 * primeiro vencimento, intervalo, entrada) e permite edição manual de
 * valores/vencimentos/comentários, redistribuindo o restante do valor nas
 * parcelas não editáveis.
 *
 * Em modo edição, `lockedRows` (parcelas pagas) são exibidas travadas e o
 * `result` deve ser o valor restante (novo total - soma das pagas).
 */
export default function QuotaEditor(props) {
  const {
    result,
    quotas,
    setquotas,
    config,
    setconfig,
    paymentType,
    lockedRows = [],
    disabled,
  } = props;

  const { formatReal, formatDate } = useFormat();
  const isAvista = paymentType === "avista";
  const lockedCount = lockedRows.length;

  // Geração das parcelas
  useEffect(() => {
    const quotaValue = isAvista ? 1 : parseInt(config?.quota_value) || 0;
    if (result > 0 && quotaValue >= 1 && !!config?.first_quota_date) {
      let _quotas = [];
      const today = new Date();

      if (config?.has_entry && !isAvista) {
        _quotas.push({
          label: "Entrada",
          value: null,
          due_date: today,
          description: "",
          is_entry: true,
          editable: true,
        });
      }

      const _value_per_quota = result / quotaValue || 0;

      for (let __i = 0; __i < quotaValue; __i++) {
        const _due_date = !(config?.quota_interval > 0)
          ? new Date(
              config?.first_quota_date.getFullYear(),
              config?.first_quota_date.getMonth() + __i,
              config?.first_quota_date.getDate(),
            )
          : new Date(
              config?.first_quota_date.getFullYear(),
              config?.first_quota_date.getMonth(),
              config?.first_quota_date.getDate() + __i * config?.quota_interval,
            );
        const _label = isAvista
          ? "A Vista"
          : `${lockedCount + __i + 1}/${lockedCount + quotaValue}`;
        _quotas.push({
          label: _label,
          due_date: isAvista ? config?.first_quota_date : _due_date,
          description: "",
          value: _value_per_quota,
        });
      }
      setquotas(_quotas);
    } else {
      setquotas([]);
    }
    // 'quotas' é atualizado por este próprio efeito; regerar apenas quando a
    // configuração ou o valor mudarem
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    result,
    isAvista,
    config?.quota_value,
    config?.first_quota_date,
    config?.quota_interval,
    config?.has_entry,
    lockedCount,
  ]);

  const checkQuotaInput = (__value, __index) => {
    let _quotas = [...quotas];
    _quotas[__index].value = __value;
    const _editable_sum = _quotas
      ?.filter(__quota => !!__quota?.editable)
      ?.reduce((__acc, __quota) => __acc + (__quota?.value || 0), 0);
    if (result - _editable_sum < 0) {
      toast.error(`A soma das parcelas não pode ultrapassar o valor a distribuir! ${formatReal(result)}`, {
        toastId: "sum_max",
      });
      return false;
    }
    return true;
  };

  // Redistribuição: parcelas não-editáveis recebem o restante
  const editableQuotasKey = JSON.stringify(quotas?.filter(__quota => __quota?.editable));
  useEffect(() => {
    if (quotas?.length > 0) {
      const _editable_sum = quotas
        ?.filter(__quota => !!__quota.editable)
        ?.reduce((__acc, __quota) => __acc + (__quota?.value || 0), 0);
      const _not_editable_length = quotas?.filter(__quota => !__quota.editable)?.length;
      const _rest = result - _editable_sum;
      setquotas(
        quotas.map(__quota =>
          !__quota.editable
            ? {
                ...__quota,
                value: _rest / _not_editable_length,
              }
            : __quota,
        ),
      );
    }
    // depender do array completo criaria loop infinito; editableQuotasKey é o gatilho deliberado
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editableQuotasKey, result]);

  return (
    <>
      <Row>
        <Col>
          <div className="divisor mt-3 mb-3"></div>
          <h2 className="mini-title">Parcelamento:</h2>
          <p className="small-label mb-2">
            Você pode customizar os valores e datas do parcelamento. Deixe também algum comentário caso precise recordar
            de algo relacionado à parcela.
          </p>
        </Col>
      </Row>
      <Row className="mt-2">
        {!isAvista && (
          <Col sm={3}>
            <span>
              Parcelamento <Required />
            </span>
            <IntegerNumber
              disabled={!!disabled}
              required={true}
              value={config?.quota_value}
              valueChange={_ev => {
                setconfig({
                  ...config,
                  quota_value: _ev,
                });
              }}
              onBlur={__ev => {
                if (!(__ev.target?.value > 0)) {
                  setconfig({
                    ...config,
                    quota_value: 1,
                  });
                }
              }}
              allowNegative={false}
              maxlength="2"
              placeholder="0"
            />
          </Col>
        )}
        <Col sm={3}>
          <span>
            {isAvista ? "Vencimento" : "Primeiro Vencimento"} <Required />
          </span>
          <DatePicker
            disabled={!!disabled}
            required={true}
            selected={config?.first_quota_date}
            dateFormat="dd/MM/yyyy"
            placeholder="--/--/----"
            changeDate={__ev => {
              setconfig({
                ...config,
                first_quota_date: __ev,
              });
            }}
          />
        </Col>
        {!isAvista && (
          <>
            <Col sm={3}>
              <div className="position-relative">
                <span>Intervalo</span>
                {config?.quota_interval === null || config?.quota_interval === undefined ? (
                  <input type="text" disabled="true" value="Mensal" />
                ) : (
                  <IntegerNumber
                    disabled={!!disabled}
                    value={config?.quota_interval}
                    valueChange={__ev => {
                      setconfig({
                        ...config,
                        quota_interval: parseInt(__ev) || "",
                      });
                    }}
                    onBlur={__ev => {
                      if (!__ev?.target?.value || __ev?.target?.value?.[0] === "0") {
                        setconfig({
                          ...config,
                          quota_interval: null,
                        });
                      }
                    }}
                    allowNegative={false}
                    placeholder="0 dias"
                    suffix={" dias"}
                  />
                )}
                <label className="im-checkbox-2">
                  <Checkbox
                    checked={!!config?.quota_interval}
                    onChange={__ev => {
                      if (!disabled)
                        setconfig({
                          ...config,
                          quota_interval: __ev.target.checked ? 30 : null,
                        });
                    }}
                  />
                  <span>Em Dias</span>
                </label>
              </div>
            </Col>
            {lockedCount === 0 && (
              <Col sm={3}>
                <span>Possui Entrada?</span>
                <Slct
                  isDisabled={!!disabled}
                  options={[
                    { value: false, label: "Não" },
                    { value: true, label: "Sim" },
                  ]}
                  value={config?.has_entry || false}
                  onChange={__ev => {
                    setconfig({
                      ...config,
                      has_entry: __ev.value,
                    });
                  }}
                  placeholder="..."
                />
              </Col>
            )}
          </>
        )}
      </Row>
      {(quotas.length > 0 || lockedRows.length > 0) && (
        <Row className="mt-4">
          <Col>
            <TableResponsive
              table={{
                head: [
                  { data: "Editar" },
                  { data: "Valor *" },
                  { data: "Vencimento *" },
                  { data: "Comentário" },
                ],
                body: [
                  ...lockedRows.map((__locked, __locked_i) => ({
                    data: [
                      {
                        element: (
                          <div className="d-flex align-items-center">
                            <span style={{ whiteSpace: "nowrap" }} className="me-2">
                              {__locked.label || `${__locked_i + 1}`}
                            </span>
                            <StatusFlag status="Pago" />
                          </div>
                        ),
                      },
                      { element: <span>{formatReal(__locked.value)}</span> },
                      { element: <span>{formatDate(__locked.due_date, 7)}</span> },
                      { element: <span>{__locked.description || "-"}</span> },
                    ],
                    disabled: true,
                    key: `locked-${__locked_i}`,
                  })),
                  ...(quotas?.map((__quota, __quota_i) => {
                    return {
                      data: [
                        {
                          element: (
                            <div className="d-flex">
                              {((!__quota.is_entry &&
                                quotas?.filter(__q => !__q.editable && !__q.is_entry)?.length > 1) ||
                                (!!__quota.editable && !__quota.is_entry)) && (
                                <Checkbox
                                  checked={__quota.editable}
                                  onChange={__ev => {
                                    if (!disabled) {
                                      let _quotas = [...quotas];
                                      _quotas[__quota_i].editable = __ev.target.checked;
                                      setquotas(_quotas);
                                    }
                                  }}
                                />
                              )}
                              <span style={{ whiteSpace: "nowrap" }} className="ml-2">
                                {__quota.label}
                              </span>
                            </div>
                          ),
                        },
                        {
                          element: (
                            <Currency
                              required={true}
                              disabled={!__quota.editable || !!disabled}
                              value={__quota.value || ""}
                              allowNegative={false}
                              isAllowed={__ev => {
                                return !!checkQuotaInput(__ev, __quota_i);
                              }}
                              valueChange={__ev => {
                                if (!!__quota.editable) {
                                  setquotas(
                                    quotas.map((__q, __i) =>
                                      __i === __quota_i
                                        ? {
                                            ...__q,
                                            value: __ev,
                                          }
                                        : __q,
                                    ),
                                  );
                                }
                              }}
                            />
                          ),
                        },
                        {
                          element: (
                            <DatePicker
                              disabled={!!disabled}
                              required={true}
                              selected={__quota?.due_date || null}
                              dateFormat="dd/MM/yyyy"
                              placeholder="--/--/----"
                              changeDate={__ev => {
                                let _quotas = [...quotas];
                                _quotas[__quota_i].due_date = __ev;
                                setquotas(_quotas);
                              }}
                            />
                          ),
                        },
                        {
                          element: (
                            <InputText
                              disabled={!!disabled}
                              value={__quota?.description}
                              onChange={__ev => {
                                let _quotas = [...quotas];
                                _quotas[__quota_i].description = __ev.target.value;
                                setquotas(_quotas);
                              }}
                            />
                          ),
                        },
                      ],
                      key: `quota-${__quota_i}`,
                    };
                  }) || []),
                ],
              }}
            />
          </Col>
        </Row>
      )}
    </>
  );
}
